// client/src/features/contracts/enums.ts — the leasing wire enums, as runtime lists · CON-005.
//
// Same construction as `features/properties/enums.ts` and `features/tenants/enums.ts`, and for
// the same reason: the generated client gives each enum as a string-literal UNION, and a union
// has no runtime value — a `<select>` cannot iterate it and `z.enum()` cannot validate against
// it. Each list is therefore spelled out once and HELD to its union by an exhaustive
// `Record<T, true>`, so a member CON-001 adds and this file lacks — or one this file keeps
// after the contract dropped it — is a compile error in the PR that regenerated the client.
//
// Labels stay in `romanian.json` under `enums.{EnumName}.{VALUE}` (04 §9.6.3).

import type {
  AmendmentType,
  ContractDocumentKind,
  ContractFxRule,
  ContractPeriodicity,
  ContractStatus,
  DepositStatus,
  IndexationType,
  PenaltyRuleType,
  RentScheduleStatus,
  UtilityResponsibility,
} from '@/client';

/** 03 §8.9's five, in the order the status machine walks them (CON-001's `ContractStatusMachine`). */
const CONTRACT_STATUS_ORDER: Record<ContractStatus, true> = {
  DRAFT: true,
  ACTIVE: true,
  EXPIRED: true,
  TERMINATED: true,
  CANCELLED: true,
};

export const CONTRACT_STATUSES = Object.keys(CONTRACT_STATUS_ORDER) as readonly ContractStatus[];

/**
 * 03 §8.4's FX rule — *which day's* rate turns a foreign-currency rent into lei. `NONE` is the
 * only legal value on a RON rent (422 `contract-fx-rule-invalid`), and `FIXED_RATE` is the only
 * one that carries a number.
 */
const FX_RULE_ORDER: Record<ContractFxRule, true> = {
  NONE: true,
  BNR_DUE_DATE: true,
  BNR_DAY_BEFORE_PAYMENT: true,
  FIXED_RATE: true,
};

export const CONTRACT_FX_RULES = Object.keys(FX_RULE_ORDER) as readonly ContractFxRule[];

/** How often the rent falls due — the generator's step (RENT-001). */
const PERIODICITY_ORDER: Record<ContractPeriodicity, true> = {
  MONTHLY: true,
  QUARTERLY: true,
  ANNUAL: true,
};

export const CONTRACT_PERIODICITIES = Object.keys(PERIODICITY_ORDER) as readonly ContractPeriodicity[];

/** The indexation clause. `FIXED_PERCENT` carries a percent; everything but `NONE` carries a month. */
const INDEXATION_ORDER: Record<IndexationType, true> = {
  NONE: true,
  INFLATION_RATE_RO: true,
  HICP_EU: true,
  FIXED_PERCENT: true,
  MANUAL: true,
};

export const INDEXATION_TYPES = Object.keys(INDEXATION_ORDER) as readonly IndexationType[];

/** The penalty clause. `DAILY_PERCENT` carries a rate, `FIXED` an amount; `NONE` carries neither. */
const PENALTY_ORDER: Record<PenaltyRuleType, true> = {
  NONE: true,
  DAILY_PERCENT: true,
  FIXED: true,
};

export const PENALTY_RULE_TYPES = Object.keys(PENALTY_ORDER) as readonly PenaltyRuleType[];

/** Who pays the utilities — 03 §8.4, and the input UTIL-001 (M3) reads to know whom to bill. */
const UTILITIES_ORDER: Record<UtilityResponsibility, true> = {
  TENANT: true,
  OWNER: true,
  SHARED: true,
  INCLUDED_IN_RENT: true,
};

export const UTILITY_RESPONSIBILITIES = Object.keys(UTILITIES_ORDER) as readonly UtilityResponsibility[];

/**
 * The act adițional's type. It is not decoration: `ContractAmendmentRules` (CON-001) gives each
 * type a CLOSED set of fields it may change, and a change outside that set is refused with 422
 * `amendment-field-not-allowed`. The wizard's second step is that set, and nothing else — see
 * `amendment-schema.ts`.
 */
const AMENDMENT_TYPE_ORDER: Record<AmendmentType, true> = {
  RENT_CHANGE: true,
  PERIOD_CHANGE: true,
  PARTY_CHANGE: true,
  DEPOSIT_CHANGE: true,
  INDEXATION: true,
  OTHER: true,
};

export const AMENDMENT_TYPES = Object.keys(AMENDMENT_TYPE_ORDER) as readonly AmendmentType[];

/**
 * 03 §8.7's garanție machine (CON-003), in the order it walks: received as `HELD`, then either
 * retained — once for part of it (`PARTIALLY_RETAINED`) or for all of it (`RETAINED`) — or given
 * back (`RETURNED`). `Deposit.IsOpen` is the server's word on whether it may still move; this
 * list exists so `StatusBadge` can be typed over it and so a member CON-003 adds cannot arrive
 * without a Romanian label.
 */
const DEPOSIT_STATUS_ORDER: Record<DepositStatus, true> = {
  HELD: true,
  PARTIALLY_RETAINED: true,
  RETAINED: true,
  RETURNED: true,
};

export const DEPOSIT_STATUSES = Object.keys(DEPOSIT_STATUS_ORDER) as readonly DepositStatus[];

/**
 * 03 §8.6's scadență states (RENT-002), as the „Program chirii" tab renders them. `CANCELLED` is
 * what a termination does to the periods past its date — they are kept with a reason, never
 * deleted (RENT-001's D-610) — which is why the panel renders one and does not hide it.
 */
const RENT_SCHEDULE_STATUS_ORDER: Record<RentScheduleStatus, true> = {
  PENDING: true,
  PARTIALLY_PAID: true,
  PAID: true,
  OVERDUE: true,
  CANCELLED: true,
};

export const RENT_SCHEDULE_STATUSES = Object.keys(
  RENT_SCHEDULE_STATUS_ORDER,
) as readonly RentScheduleStatus[];

/**
 * CON-004's four generated PDFs, in the order the „Documente" tab offers them: the lease
 * itself, then the three annexes a handover needs. The order is the order of use, which is why
 * it is spelled out rather than read off an object whose key order nobody guarantees.
 */
const CONTRACT_DOCUMENT_KIND_ORDER: Record<ContractDocumentKind, true> = {
  CONTRACT_DRAFT: true,
  HANDOVER_PROTOCOL: true,
  INVENTORY_ANNEX: true,
  METER_READING_SHEET: true,
};

export const CONTRACT_DOCUMENT_KINDS = Object.keys(
  CONTRACT_DOCUMENT_KIND_ORDER,
) as readonly ContractDocumentKind[];

/**
 * The currencies a V1 contract can be denominated in. `PropertyForm` has the same three and the
 * same reason (spec §5): `app.currencies` is the server's list and a 422
 * (`contract-currency-unknown`) is the enforcement — this is the offer, not the rule.
 */
export const CONTRACT_CURRENCIES = ['RON', 'EUR', 'USD'] as const;
